import { Column, Entity, Index, JoinColumn, ManyToOne, OneToMany, PrimaryGeneratedColumn } from 'typeorm';
import { CustomizationItemDto } from './models/customization/customization.dto';
import { CreativeUnitDto } from './models/creative-unit/creative-unit.dto';
import { LayerDto } from './models/layer/layer.dto';
import { AnimationConfigDto, AnimationDto } from './models/animation.dto';
import { CategoryDto } from './models/category.dto';
import { AssetDto } from '../asset/models/asset.dto';
import { ThemeDto } from './models/theme.dto';
import { MissingFontMarkerDto } from './models/missing-font.dto';
import { StylesConfigDto } from './models/styles.dto';
import { PrintConfigDto } from './models/print.dto';
import { AdvancedConfigDto } from './models/advanced-config.dto';
import { CreativeUnitListingDto, CreativeUnitListingSettings } from './models/creative-unit/creative-unit-listing.dto';
import { PublicUser, User } from '../user/user.entity';
import { LocaleDto } from './models/locale.dto';
import { ExportType, HtmlFormat, ImageFormat, OutputConfig } from '../encode/models/job-def.dto';
import { InteractivityDto } from './models/interactivity.dto';
import { StartUpWizardDto } from './models/startup-wizard/startup-wizard.dto';
import { CreativePackageBusiness } from './creative-package-business/creative-package-business.entity';
import { BackgroundDto } from './models/creative-unit/creative-unit.dto';
import { CartItemType } from '../cart/project-cart-item.entity';
import { Business } from '../business/business.entity';
import { unionBy } from 'lodash';
import { EncodeJob, PublicEncodeJob } from '../encode/encode-job.entity';
import { ScriptDto } from './models/dto/script.dto';
import { PackageMetadata } from './validators/package-metadata.validator';

export enum Status {
	Draft = 'draft',
	Published = 'published'
}

export enum SortStrategy {
	ASC = 'ASC',
	DESC = 'DESC'
}

export enum SortString {
	Name = 'name',
	Created = 'created'
}

export enum SummarySortString {
	Name = 'name',
	Modified = 'modified',
	CreatedByUserId = 'createdByUserId',
	Description = 'description'
}

export namespace ExportTypes {
	export function getExportTypeFileFormat(exportType: ExportType | HtmlFormat, outputConfig?: OutputConfig) {
		switch (exportType) {
			case ExportType.STATIC:
				if (outputConfig?.imageFormat === ImageFormat.SVG) {
					return 'SVG';
				}
				if (outputConfig?.imageFormat === ImageFormat.AI) {
					return 'AI';
				}
				if (outputConfig?.imageFormat === ImageFormat.PSD) {
					return 'PSD';
				}
				if (outputConfig?.imageFormat === ImageFormat.PNG) {
					return 'PNG';
				}
				return 'JPG';
			case ExportType.VIDEO:
				return 'MP4';
			case ExportType.ANIMATED_GIF:
				return 'GIF';
			case ExportType.HTML5:
				return 'HTML5';
			case ExportType.AUDIO:
				return 'MP3';
			case ExportType.PRINT:
				return 'PDF';
			case HtmlFormat.HTML:
				return 'HTML';
			case HtmlFormat.Email:
				return 'EML';
			case HtmlFormat.Text:
				return 'TXT';
			default:
				return;
		}
	}

	export function getExportTypeTitle(exportType: ExportType) {
		switch (exportType) {
			case ExportType.STATIC:
				return 'Static';
			case ExportType.VIDEO:
				return 'Video';
			case ExportType.HTML5:
				return 'HTML5';
			case ExportType.AUDIO:
				return 'Audio';
			case ExportType.PRINT:
				return 'Print';
			case ExportType.EMAIL:
				return 'Email';
			case ExportType.ANIMATED_GIF:
				return 'Animated GIF';
			default:
				return;
		}
	}
}

export type PublicCreativeUnitPackage = Pick<
	CreativeUnitPackage,
	| 'id'
	| 'name'
	| 'description'
	| 'aiInstructions'
	| 'categories'
	| 'region'
	| 'status'
	| 'tags'
	| 'metadata'
	| 'customizations'
	| 'creativeUnits'
	| 'layers'
	| 'containerClasses'
	| 'styles'
	| 'variables'
	| 'animation'
	| 'animationConfig'
	| 'printConfig'
	| 'htmlConfig'
	| 'thumbnail'
	| 'startUpWizard'
	| 'theme'
	| 'missingFonts'
	| 'interactivity'
	| 'disableLasso'
	| 'exportTypes'
	| 'creativeUnitListingSettings'
	| 'advancedConfig'
	| 'scripts'
	| 'locale'
	| 'publishedAt'
	| 'modified'
	| 'originalVersionId'
	| 'versionComment'
	| 'validationErrors'
	| 'background'
	| 'isPinned'
> & {
	versions?: PublicCreativeUnitPackage[];
	createdByUser?: PublicUser | Partial<PublicUser>;
	lastModifiedByUser?: PublicUser | Partial<PublicUser>;
	publishedByUser?: PublicUser | Partial<PublicUser>;
	isVersion: boolean;
	businesses?: Partial<Business>[];
	encodeJobs?: Partial<PublicEncodeJob>[];
};

export type PublicCreativeUnitPackageSummary = Pick<
	CreativeUnitPackage,
	| 'id'
	| 'name'
	| 'exportTypes'
	| 'thumbnail'
	| 'publishedAt'
	| 'description'
	| 'modified'
	| 'background'
	| 'originalVersionId'
	| 'metadata'
> & {
	creativeUnitsNames: string[];
	visibleCreativeUnitsNames: { name: string; locale: string | LocaleDto }[];
	createdByUser?: PublicUser | Partial<PublicUser>;
	lastModifiedByUser?: PublicUser | Partial<PublicUser>;
	publishedByUser?: PublicUser | Partial<PublicUser>;
	creativeUnits?: any;
	isPinned?: boolean;
	latestPublishedVersionId?: string;
	creativePackageBusinesses?: Partial<Business>[];
};

export type CreativeUnitPackageSummary = Pick<CreativeUnitPackage, 'id' | 'name' | 'thumbnail' | 'modified' | 'background'> & {
	creativeUnitsNames: string[];
	description: string;
	createdByUser?: PublicUser | Partial<PublicUser>;
	lastModifiedByUser?: PublicUser | Partial<PublicUser>;
	publishedByUser?: PublicUser | Partial<PublicUser>;
};

// Package version metadata for the version dropdown in the editor
export type PublicCreativeUnitPackageVersion = Pick<
	CreativeUnitPackage,
	'id' | 'name' | 'status' | 'created' | 'modified' | 'versionComment' | 'originalVersionId'
> & {
	createdByUser?: Pick<PublicUser, 'fullName' | 'email'>;
	lastModifiedByUser?: Pick<PublicUser, 'fullName' | 'email'>;
	publishedByUser?: Pick<PublicUser, 'fullName' | 'email'>;
};

@Entity('creativePackage')
@Index('idx_creative_package_dynamic_link', ['modified'], {
	where: '"deleted" IS FALSE AND "customizations" IS NOT NULL AND "customizations"::text LIKE \'%"dynamicLink"%\''
})
export class CreativeUnitPackage {
	constructor(value?: Partial<CreativeUnitPackage>) {
		for (const k in value) {
			this[k] = value[k];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	public id: string;

	@Column('text', { nullable: true, default: 'New Package' })
	public name: string;

	@Column('text', { nullable: true })
	public description: string;

	@Column('text', { nullable: true })
	public aiInstructions: string;

	@Column('jsonb', { nullable: true })
	public categories: { [key: CategoryDto['slug']]: CategoryDto };

	@Column('text', { nullable: true })
	public region: string;

	@Column({
		type: 'enum',
		enum: Status,
		default: Status.Draft,
		nullable: false
	})
	public status: Status;

	@Column('jsonb', { nullable: true })
	public tags: string[] | null;

	// Author-defined package-level metadata (plan U1 / R1): a flat string map, the
	// same shape creative units already carry, bounded by `IsPackageMetadata` on
	// the DTO. Deliberately no column default — the migration verify gate diffs
	// entity metadata against `schema:log` and would demand a matching DEFAULT
	// clause on the column.
	@Column('jsonb', { nullable: true })
	public metadata: PackageMetadata | null;

	@Column('jsonb', { nullable: true })
	public customizations: CustomizationItemDto[] | null;

	@Column('jsonb', { nullable: true })
	public creativeUnits: CreativeUnitDto[] | null;

	@Column('jsonb', { nullable: true })
	public layers: LayerDto[] | null;

	@Column('text', { nullable: true })
	public containerClasses?: string;

	@Column('jsonb', { nullable: true })
	public styles: string | StylesConfigDto[] | null;

	@Column('jsonb', { nullable: true })
	public variables: object | null;

	@Column('jsonb', { nullable: true })
	public animation: AnimationDto | null;

	@Column('jsonb', { nullable: true })
	public animationConfig: AnimationConfigDto | null;

	@Column('jsonb', { nullable: true })
	public printConfig: PrintConfigDto | null;

	@Column('jsonb', { nullable: true })
	public thumbnail: AssetDto | null;

	@Column('jsonb', { nullable: true })
	public startUpWizard: StartUpWizardDto | null;

	@Column('jsonb', { nullable: true, default: ['static'] })
	public exportTypes: ExportType[] | null;

	@Column('jsonb', { nullable: true })
	public theme: ThemeDto | null;

	// Per-layer markers for PSD fonts with no owned match (plan U7); consumed by
	// the "resolve fonts" recovery action (U8).
	@Column('jsonb', { nullable: true })
	public missingFonts: MissingFontMarkerDto[] | null;

	@Column('jsonb', { nullable: true })
	public interactivity?: InteractivityDto | null;

	@Column('jsonb', { nullable: true })
	public htmlConfig?: import('./models/html-config.dto').HtmlConfigDto | null;

	@Column('boolean', { nullable: true })
	public disableLasso?: boolean | null;

	@Column('boolean', { default: false })
	public deleted: boolean;

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public created: string;

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public modified: string;

	@Column({ type: 'timestamptz', nullable: true })
	public publishedAt: string;

	@Column('text', { nullable: true })
	public priceProductId: string;

	@Column('jsonb', { nullable: true })
	creativeUnitListingSettings?: CreativeUnitListingSettings;

	@Column('jsonb', { nullable: true })
	public advancedConfig: AdvancedConfigDto | null;

	@Column('jsonb', { nullable: true })
	public scripts: ScriptDto[] | null;

	@Column('jsonb', { nullable: true })
	locale?: LocaleDto;

	@Column('uuid', { nullable: true })
	public createdByUserId?: string;
	@ManyToOne(() => User, {
		eager: false,
		nullable: true,
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'createdByUserId' })
	public createdByUser?: User | Partial<User>;

	@Column('uuid', { nullable: true })
	public lastModifiedById?: string;
	@ManyToOne(() => User, {
		eager: false,
		nullable: true,
		onDelete: 'SET NULL'
	})
	@JoinColumn({ name: 'lastModifiedById' })
	public lastModifiedByUser?: User | Partial<User>;

	@Column('uuid', { nullable: true })
	public publishedById?: string;
	@ManyToOne(() => User, {
		eager: false,
		nullable: true,
		onDelete: 'SET NULL'
	})
	@JoinColumn({ name: 'publishedById' })
	public publishedByUser?: User | Partial<User>;

	// Non-persistent property for pin and latestPublished status (populated via query)
	public isPinned?: boolean;
	public latestPublishedVersionId?: string;

	@Column('uuid', { nullable: true })
	public originalVersionId?: string;

	@Index('idx_creative_package_original_version_id')
	@Column({
		type: 'uuid',
		asExpression: 'COALESCE("originalVersionId", id)',
		generatedType: 'STORED',
		insert: false,
		update: false,
		select: false
	})
	public versionGroupId: string;

	@Column('text', { nullable: true })
	public versionComment?: string;

	@ManyToOne(
		() => CreativeUnitPackage,
		pkg => pkg.versions,
		{ eager: false, nullable: true, onDelete: 'SET NULL' }
	)
	@JoinColumn({ name: 'originalVersionId' })
	public originalVersion?: CreativeUnitPackage;

	@OneToMany(
		() => CreativeUnitPackage,
		pkg => pkg.originalVersion
	)
	public versions?: CreativeUnitPackage[];

	@OneToMany(
		() => CreativePackageBusiness,
		cpb => cpb.creativePackage,
		{ cascade: true }
	)
	public creativePackageBusinesses?: CreativePackageBusiness[];

	@Column('jsonb', { nullable: true })
	public validationErrors?: { [key: string]: string };

	@Column('jsonb', { nullable: true })
	public businesses?: Partial<Business>[];

	@Column('jsonb', { nullable: true })
	public background: BackgroundDto | null;

	@OneToMany(
		() => EncodeJob,
		job => job.package,
		{
			eager: false,
			nullable: true
		}
	)
	public encodeJobs?: EncodeJob[] | Partial<EncodeJob>[] | PublicEncodeJob[];

	public toPublic() {
		// merge the existing relations with the "businesses" entity field to ensure some portability between environments
		const businessRelations: Partial<Business>[] = this.creativePackageBusinesses?.map(cpb => {
			return {
				id: cpb.business?.id,
				name: cpb.business.name
			};
		});
		const businesses = unionBy(businessRelations, this.businesses, 'id');

		const pub: Partial<PublicCreativeUnitPackage> = {
			id: this.id,
			name: this.name,
			description: this.description,
			aiInstructions: this.aiInstructions,
			categories: this.categories,
			region: this.region,
			status: this.status,
			tags: this.tags,
			metadata: this.metadata,
			customizations: this.customizations,
			creativeUnits: this.creativeUnits,
			layers: this.layers,
			containerClasses: this.containerClasses,
			styles: this.styles,
			variables: this.variables,
			animation: this.animation,
			animationConfig: this.animationConfig,
			printConfig: this.printConfig,
			htmlConfig: this.htmlConfig,
			thumbnail: this.thumbnail,
			startUpWizard: this.startUpWizard,
			theme: this.theme,
			missingFonts: this.missingFonts,
			interactivity: this.interactivity,
			disableLasso: this.disableLasso,
			publishedAt: this.publishedAt,
			exportTypes: this.exportTypes,
			creativeUnitListingSettings: this.creativeUnitListingSettings,
			advancedConfig: this.advancedConfig,
			modified: this.modified,
			scripts: this.scripts,
			locale: this.locale,
			originalVersionId: this.originalVersionId,
			isVersion: !!this.originalVersionId,
			versionComment: this.versionComment,
			versions: this.versions?.map(v => v.toPublic()),
			businesses,
			validationErrors: this.validationErrors,
			background: this.background,
			isPinned: this.isPinned
		};
		//console.log('created by user', this.createdByUser);
		if (this.createdByUser) {
			const publicUser = this.createdByUser.toPublic();

			pub.createdByUser = {
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.lastModifiedByUser) {
			const publicUser = this.lastModifiedByUser.toPublic();

			pub.lastModifiedByUser = {
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.publishedByUser) {
			const publicUser = this.publishedByUser.toPublic();

			pub.publishedByUser = {
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.encodeJobs?.length) {
			pub.encodeJobs = this.encodeJobs.map(job => job.toPublic(['project', 'encodeTasks']));
		}

		return pub as PublicCreativeUnitPackage;
	}

	public toPublicSummary(includeUnits: boolean = false) {
		const exportTypes = Array.from(
			new Set(
				(this.creativeUnits ?? [])
					.map(cu => cu.exportTypes)
					.flat()
					.filter(Boolean)
			)
		);

		const visibleCreativeUnitNameMap = new Map<string, { name: string; locale: string | LocaleDto }>();
		const units = [];

		this.creativeUnits
			?.filter(cu => !cu.hidden)
			.forEach(cu => {
				visibleCreativeUnitNameMap.set(cu.name, {
					name: cu.name,
					locale: cu.locale
				});

				if (includeUnits) {
					units.push(this.createItem(cu, null));
				}
			});

		if (this.creativeUnitListingSettings?.listings) {
			this.creativeUnitListingSettings.listings
				.filter(l => !l.hidden)
				.forEach(l => {
					visibleCreativeUnitNameMap.set(l.name, {
						name: l.name,
						locale: { ...visibleCreativeUnitNameMap.get(l.name), ...l.locale }
					});

					if (includeUnits) {
						// Group members are emitted even when hidden — `hidden` only suppresses
						// a size's own card, which the standalone loop above handles. Optional
						// chaining: children-only listings declare no creativeUnitIds.
						l.creativeUnitIds?.forEach(c => {
							const unitMatch = this.creativeUnits?.find(cu => cu.id === c);

							if (unitMatch) {
								units.push(this.createItem(unitMatch, l));
							}
						});
					}
				});
		}

		const visibleCreativeUnitsNames: { name: string; locale: string | LocaleDto }[] =
			Array.from(visibleCreativeUnitNameMap.values()) || [];

		const businessRelations: Partial<Business>[] =
			this.creativePackageBusinesses
				?.map(cpb => ({
					id: cpb.business?.id,
					name: cpb.business?.name
				}))
				.filter(b => b.id) ?? [];

		const pub: Partial<PublicCreativeUnitPackageSummary> = {
			id: this.id,
			name: this.name,
			thumbnail: this.thumbnail,
			creativeUnitsNames: this.creativeUnits?.map(cu => cu.name) || [],
			visibleCreativeUnitsNames: visibleCreativeUnitsNames,
			exportTypes: exportTypes.length ? exportTypes : this.exportTypes,
			publishedAt: this.publishedAt,
			description: this.description,
			modified: this.modified,
			background: this.background,
			// Package-level metadata (U1). Distinct from the per-unit
			// `creativeUnits[].metadata` emitted below when `includeUnits` is set —
			// the two shapes are independent and must not be merged.
			metadata: this.metadata,
			isPinned: this.isPinned,
			originalVersionId: this.originalVersionId,
			latestPublishedVersionId: this.latestPublishedVersionId,
			creativePackageBusinesses: businessRelations
		};

		if (includeUnits) {
			pub.creativeUnits = units || [];
		}

		if (this.createdByUser) {
			const publicUser = this.createdByUser.toPublic();

			pub.createdByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.lastModifiedByUser) {
			const publicUser = this.lastModifiedByUser.toPublic();

			pub.lastModifiedByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.publishedByUser) {
			const publicUser = this.publishedByUser.toPublic();

			pub.publishedByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		return pub as PublicCreativeUnitPackageSummary;
	}

	public toSummary() {
		const sum: Partial<CreativeUnitPackageSummary> = {
			id: this.id,
			name: this.name,
			thumbnail: this.thumbnail,
			creativeUnitsNames: this.creativeUnits?.map(cu => cu.name) || [],
			description: this.description,
			modified: this.modified,
			background: this.background
		};

		if (this.createdByUser) {
			const publicUser = this.createdByUser.toPublic();

			sum.createdByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.lastModifiedByUser) {
			const publicUser = this.lastModifiedByUser.toPublic();

			sum.lastModifiedByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		if (this.publishedByUser) {
			const publicUser = this.publishedByUser.toPublic();

			sum.publishedByUser = {
				id: publicUser.id,
				fullName: publicUser.fullName,
				email: publicUser.email
			};
		}

		return sum as CreativeUnitPackageSummary;
	}

	/**
	 * Metadata-only for the template version dropdown.
	 */
	public toVersionMetadata(): PublicCreativeUnitPackageVersion {
		const meta: PublicCreativeUnitPackageVersion = {
			id: this.id,
			name: this.name,
			status: this.status,
			created: this.created,
			modified: this.modified,
			versionComment: this.versionComment,
			originalVersionId: this.originalVersionId
		};

		if (this.createdByUser) {
			const publicUser = this.createdByUser.toPublic();
			meta.createdByUser = { fullName: publicUser.fullName, email: publicUser.email };
		}

		if (this.lastModifiedByUser) {
			const publicUser = this.lastModifiedByUser.toPublic();
			meta.lastModifiedByUser = { fullName: publicUser.fullName, email: publicUser.email };
		}

		if (this.publishedByUser) {
			const publicUser = this.publishedByUser.toPublic();
			meta.publishedByUser = { fullName: publicUser.fullName, email: publicUser.email };
		}

		return meta;
	}

	private createItem(unit: CreativeUnitDto, listing?: CreativeUnitListingDto) {
		let obj = {
			id: unit.id,
			name: unit.name,
			locale: unit.locale,
			dimensions: unit.dimensions,
			metadata: {
				packageId: unit.packageId
			},
			type: CartItemType.AD_UNIT
		};

		if (listing) {
			obj = {
				...obj,
				locale: {
					...listing?.locale,
					...obj.locale,
					group: {
						...listing.locale
					}
				},
				metadata: {
					...obj.metadata,
					group: {
						id: listing.id,
						name: listing.name,
						locale: listing.locale,
						instanceId: listing.id
					}
				}
			} as any;
		}

		return obj;
	}
}
